import { CaretDown, Info } from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Card,
  DropdownMenu,
  Flex,
  Grid,
  Heading,
  Skeleton,
  Text,
  TextField,
  Tooltip,
} from "@radix-ui/themes";
import { useState } from "react";

import { AutomationTable } from "./components/AutomationTable";
import { SummaryCards } from "./components/SummaryCards";
import { roiRoute } from "./route";

import { useRoiDashboard } from "../../utils/queries/roiDashboard";

// Skeleton component for Summary Cards
function SummaryCardsSkeleton() {
  return (
    <Flex direction="column" gap="4">
      <Grid columns="2" gap="4">
        <Card className="flex flex-col bg-base-white">
          <Skeleton className="h-5 w-40" />
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="2"
            className="flex-1 py-8"
          >
            <Skeleton className="h-12 w-24" />
            <Skeleton className="h-5 w-48" />
          </Flex>
        </Card>
        <Card className="bg-base-white">
          <Skeleton className="mb-3 h-5 w-56" />
          <Flex gap="3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="flex-1">
                <Skeleton className="mb-2 h-4 w-24" />
                <Flex justify="center" className="py-4">
                  <Skeleton className="h-10 w-12" />
                </Flex>
              </Card>
            ))}
          </Flex>
        </Card>
      </Grid>
      {/* Summary stats paragraph skeleton */}
      <Skeleton className="h-4 w-[500px]" />
    </Flex>
  );
}

// Skeleton component for Automation Table (single combined table)
function AutomationTableSkeleton() {
  return (
    <div className="bg-white rounded-md border border-puntt-neutral-gray-6">
      {/* Header */}
      <div className="border-b border-puntt-neutral-gray-6 bg-puntt-neutral-gray-3 p-3">
        <Flex gap="4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-20" />
        </Flex>
      </div>
      {/* Rows */}
      {[1, 2, 3, 4, 5, 6, 7, 8].map((row) => (
        <div
          key={row}
          className="border-b border-puntt-neutral-gray-6 p-3 last:border-b-0"
        >
          <Flex gap="4">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-8" />
            <Skeleton className="h-4 w-8" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-10" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
          </Flex>
        </div>
      ))}
    </div>
  );
}

// Date range preset options
type DatePreset =
  | "all_time"
  | "this_week"
  | "this_month"
  | "this_quarter"
  | "year_to_date"
  | "custom";

const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  all_time: "All Time",
  this_week: "This Week",
  this_month: "This Month",
  this_quarter: "This Quarter",
  year_to_date: "Year to Date",
  custom: "Custom Range...",
};

function getDateRangeFromPreset(preset: DatePreset): {
  startDate: string | undefined;
  endDate: string | undefined;
} {
  const now = new Date();
  const today = now.toISOString().split("T")[0];

  switch (preset) {
    case "all_time":
      return { startDate: undefined, endDate: undefined };
    case "this_week": {
      const dayOfWeek = now.getDay();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - dayOfWeek);
      return {
        startDate: startOfWeek.toISOString().split("T")[0],
        endDate: today,
      };
    }
    case "this_month": {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      return {
        startDate: startOfMonth.toISOString().split("T")[0],
        endDate: today,
      };
    }
    case "this_quarter": {
      const quarter = Math.floor(now.getMonth() / 3);
      const startOfQuarter = new Date(now.getFullYear(), quarter * 3, 1);
      return {
        startDate: startOfQuarter.toISOString().split("T")[0],
        endDate: today,
      };
    }
    case "year_to_date": {
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      return {
        startDate: startOfYear.toISOString().split("T")[0],
        endDate: today,
      };
    }
    case "custom":
      return { startDate: undefined, endDate: undefined };
    default:
      return { startDate: undefined, endDate: undefined };
  }
}

export default function ROIView() {
  const { startDate, endDate } = roiRoute.useSearch();
  const navigate = roiRoute.useNavigate();

  // Local state for date filter
  const [selectedPreset, setSelectedPreset] = useState<DatePreset>("all_time");
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(startDate ?? "");
  const [customEndDate, setCustomEndDate] = useState(endDate ?? "");

  // Fetch ROI dashboard stats (real-time computed)
  const {
    data: roiDashboard,
    isLoading,
    isError,
    error,
  } = useRoiDashboard({
    startDate: startDate ?? undefined,
    endDate: endDate ?? undefined,
  });

  const handlePresetSelect = (preset: DatePreset) => {
    setSelectedPreset(preset);

    if (preset === "custom") {
      setShowCustomRange(true);
      return;
    }

    setShowCustomRange(false);
    const { startDate: newStart, endDate: newEnd } =
      getDateRangeFromPreset(preset);
    navigate({
      search: {
        startDate: newStart,
        endDate: newEnd,
      },
      replace: true,
    });
  };

  const handleApplyCustomRange = () => {
    navigate({
      search: {
        startDate: customStartDate || undefined,
        endDate: customEndDate || undefined,
      },
      replace: true,
    });
  };

  const handleCancelCustomRange = () => {
    setShowCustomRange(false);
    setSelectedPreset("all_time");
    setCustomStartDate("");
    setCustomEndDate("");
  };

  if (isError) {
    return (
      <article
        className="flex flex-col gap-6 overflow-auto px-10 py-6"
        data-testid="roi-dashboard"
      >
        <Heading as="h1" size="7" weight="bold">
          Automation Status
        </Heading>
        <Text size="3" color="red">
          Error loading stats: {(error as Error)?.message || "Unknown error"}
        </Text>
      </article>
    );
  }

  // Map category IDs to display names
  const categoryDisplayNames: Record<string, string> = {
    AT1: "Previous Feedback Implemented",
    AT2: "Required Legal/Regulatory Text",
    AT3: "Copy Errors",
    AT4: "Deliverable Specifications",
    AT5: "Brand Logo Usage",
    AT6: "SKU/Product Variant",
    AT7: "Legal Statements Accuracy",
    AT8: "Claims Alignment",
    AT9: "Layout vs Brand Guidelines",
    AT10: "Retailer/Channel Requirements",
  };

  // Map category IDs to descriptions for tooltips
  const categoryDescriptions: Record<string, string> = {
    AT1: "Checks if feedback from previous reviews has been implemented in the current version",
    AT2: "Checks for presence of required legal disclaimers and regulatory text",
    AT3: "Detects spelling, grammar, punctuation, and copy inconsistencies",
    AT4: "Validates format, dimensions, and file type against the original brief",
    AT5: "Validates correct brand logo version, placement, color, and spacing",
    AT6: "Ensures correct product, packshot, size, or variant is shown",
    AT7: "Validates dates, versions, and accuracy of required legal statements",
    AT8: "Verifies marketing claims match approved substantiation and wording",
    AT9: "Checks layout hierarchy, CTA prominence, spacing, and color against brand guidelines",
    AT10: "Ensures content meets retailer- or channel-specific rules and requirements",
  };

  // Transform API data to component format - no mock data fallback
  const summary = roiDashboard
    ? {
        currentAutomationPercent: roiDashboard.automationPercentage,
        fullyAutomatedCount: roiDashboard.breakdownNumbers.full,
        inTrainingCount: roiDashboard.breakdownNumbers.training,
        outOfScopeCount: roiDashboard.breakdownNumbers.outOfScope,
      }
    : {
        currentAutomationPercent: 0,
        fullyAutomatedCount: 0,
        inTrainingCount: 0,
        outOfScopeCount: 0,
      };

  const tasks = roiDashboard
    ? roiDashboard.results.map((task, index) => ({
        id: `${task.status}-${task.categoryId}-${index}`,
        categoryId: task.categoryId,
        taskCategory:
          categoryDisplayNames[task.categoryId] ||
          task.categoryName ||
          task.categoryId,
        description: categoryDescriptions[task.categoryId],
        errorFreeChecks: task.errorFreeChecks,
        humanCaughtLowRisk: task.humanCaughtLowRisk,
        humanCaughtHighRisk: task.humanCaughtHighRisk,
        aiReviewSpeed: task.aiReviewSpeed,
        auditLog: task.auditLog,
        status: task.status,
        weeksWithoutHighRisk: task.weeksWithoutHighRisk ?? 0,
      }))
    : [];

  // Plain block flow on the article (no display:grid, no display:flex)
  // so children render at their natural heights and only the article
  // scrolls. Outer wrapper clamps the article to its grid cell so
  // overflow-y-auto has something to scroll against. Same pattern as
  // settings — grid auto-rows / flex-col on the article both squish
  // children before scrolling kicks in (the recurring "HeadlineStrip
  // shows just labels, no values" regression).
  return (
    <div
      data-testid="roi-dashboard"
      className="flex h-full flex-col overflow-hidden"
    >
      <article className="flex-1 space-y-6 overflow-y-auto px-10 py-6">
        {/* Header */}
        <header className="flex items-center justify-between">
          <Flex align="center" gap="3">
            <Heading as="h1" size="7" weight="bold">
              Automation Status
            </Heading>
            <Badge color="green" size="2" variant="solid">
              SAFE
            </Badge>
            <Tooltip content="Learn more about automation status">
              <Info
                size={14}
                className="shrink-0 cursor-help text-puntt-neutral-gray-9"
              />
            </Tooltip>
          </Flex>
        </header>

        {/* Date Range Filter */}
        <Flex align="center" gap="3" wrap="wrap" data-testid="roi-date-filter">
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <Button variant="soft" color="gray">
                {DATE_PRESET_LABELS[selectedPreset]}
                <CaretDown size={14} />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              <DropdownMenu.Item
                data-testid="roi-date-preset-all_time"
                onSelect={() => handlePresetSelect("all_time")}
              >
                All Time
              </DropdownMenu.Item>
              <DropdownMenu.Item
                data-testid="roi-date-preset-this_week"
                onSelect={() => handlePresetSelect("this_week")}
              >
                This Week
              </DropdownMenu.Item>
              <DropdownMenu.Item
                data-testid="roi-date-preset-this_month"
                onSelect={() => handlePresetSelect("this_month")}
              >
                This Month
              </DropdownMenu.Item>
              <DropdownMenu.Item
                data-testid="roi-date-preset-this_quarter"
                onSelect={() => handlePresetSelect("this_quarter")}
              >
                This Quarter
              </DropdownMenu.Item>
              <DropdownMenu.Item
                data-testid="roi-date-preset-year_to_date"
                onSelect={() => handlePresetSelect("year_to_date")}
              >
                Year to Date
              </DropdownMenu.Item>
              <DropdownMenu.Separator />
              <DropdownMenu.Item
                data-testid="roi-date-preset-custom"
                onSelect={() => handlePresetSelect("custom")}
              >
                Custom Range...
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>

          {/* Custom Date Range Inputs */}
          {showCustomRange && (
            <Flex align="center" gap="2">
              <TextField.Root
                data-testid="roi-custom-start-date"
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                placeholder="Start date"
                className="w-[150px]"
              />
              <Text size="2" color="gray">
                to
              </Text>
              <TextField.Root
                data-testid="roi-custom-end-date"
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                placeholder="End date"
                className="w-[150px]"
              />
              <Button
                data-testid="view-button-407"
                variant="soft"
                size="2"
                onClick={handleApplyCustomRange}
              >
                Apply
              </Button>
              <Button
                data-testid="view-button-410"
                variant="ghost"
                color="gray"
                size="2"
                onClick={handleCancelCustomRange}
              >
                Cancel
              </Button>
            </Flex>
          )}
        </Flex>

        {/* Loading State - Show Skeletons */}
        {isLoading ? (
          <>
            <SummaryCardsSkeleton />
            <section>
              <Skeleton className="mb-6 h-8 w-64" />
              <AutomationTableSkeleton />
            </section>
          </>
        ) : (
          <>
            {/* Summary Cards - always show, with zeros if no data */}
            <SummaryCards
              summary={summary}
              summaryStats={roiDashboard?.summaryStats}
            />

            {/* Automation Review Tasks Section - always show */}
            <section>
              <Text size="7" weight="bold" className="mb-6 block">
                Automated Review Tasks
              </Text>
              <AutomationTable
                tasks={tasks}
                startDate={startDate}
                endDate={endDate}
              />
            </section>
          </>
        )}
      </article>
    </div>
  );
}
